import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import {
  fetchActiveLaunchSplashImage,
  LaunchSplashImage,
} from "@/services/launchSplashService";

const SESSION_SEEN_KEY = "launchSplashSeenImageId";

const LaunchSplashModal = () => {
  const [splashImage, setSplashImage] = useState<LaunchSplashImage | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [naturalWidth, setNaturalWidth] = useState<number | null>(null);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);
  const [displayWidth, setDisplayWidth] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const loadSplashImage = async () => {
      try {
        const activeImage = await fetchActiveLaunchSplashImage();
        if (!isMounted) return;

        if (activeImage) {
          const hasSeenInSession = (() => {
            if (typeof window === "undefined") return false;
            try {
              return sessionStorage.getItem(SESSION_SEEN_KEY) === activeImage.id;
            } catch {
              return false;
            }
          })();

          setSplashImage(activeImage);
          setIsOpen(!hasSeenInSession);
          setNaturalWidth(null);
          setNaturalHeight(null);
          setDisplayWidth(null);

          if (!hasSeenInSession) {
            try {
              sessionStorage.setItem(SESSION_SEEN_KEY, activeImage.id);
            } catch {
              // non-blocking if storage is unavailable
            }
          }
        } else {
          setSplashImage(null);
          setIsOpen(false);
          setNaturalWidth(null);
          setNaturalHeight(null);
          setDisplayWidth(null);
        }
      } catch (error) {
        console.error("Failed to fetch launch splash modal image:", error);
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    loadSplashImage();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!naturalWidth || !naturalHeight) return;
      setDisplayWidth(computeDisplayWidth(naturalWidth, naturalHeight));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [naturalWidth, naturalHeight]);

  const computeDisplayWidth = (imgWidth: number, imgHeight: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw <= 640;
    const padding = isMobile ? 24 : 48;
    const desktopCap = 1100;
    const maxWidth = isMobile ? vw - padding : Math.min(vw - padding, desktopCap);
    const maxHeight = vh * 0.9;
    const widthScale = maxWidth / imgWidth;
    const heightScale = maxHeight / imgHeight;
    const scale = Math.min(1, widthScale, heightScale);
    return Math.max(0, Math.floor(imgWidth * scale));
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNavigate = () => {
    if (!splashImage?.routePath) {
      return;
    }
    handleClose();
    navigate(splashImage.routePath);
  };

  if (isChecking || !splashImage) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        } else {
          setIsOpen(true);
        }
      }}
    >
      <DialogContent
        className="border-none bg-transparent p-0 shadow-none"
        showCloseButton={false}
        style={{
          width: displayWidth ? `${displayWidth}px` : "auto",
          maxWidth: "calc(100vw - 16px)",
        }}
      >
        <div className="relative overflow-hidden rounded-3xl shadow-[0_40px_120px_-24px_rgba(0,0,0,0.45)]">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close welcome image"
            className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white shadow-lg transition hover:bg-black focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className={`relative overflow-hidden ${
              splashImage.routePath ? "cursor-pointer" : ""
            }`}
            onClick={splashImage.routePath ? handleNavigate : undefined}
            role={splashImage.routePath ? "button" : undefined}
            tabIndex={splashImage.routePath ? 0 : undefined}
            onKeyDown={(event) => {
              if (!splashImage.routePath) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleNavigate();
              }
            }}
          >
            <img
              src={splashImage.url}
              alt={splashImage.altText || "Launch splash"}
              className="w-full h-auto max-h-[90vh] object-contain"
              loading="eager"
              onLoad={(event) => {
                const width = event.currentTarget.naturalWidth;
                const height = event.currentTarget.naturalHeight;
                setNaturalWidth(width);
                setNaturalHeight(height);
                setDisplayWidth(computeDisplayWidth(width, height));
              }}
            />
            {splashImage.routePath && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-6 pb-5 pt-10 text-left">
                <span className="text-sm font-semibold text-white">
                  Click to learn more
                </span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LaunchSplashModal;
